'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { memo, useState } from 'react'

import { useCardReviewRating } from '@/features/product-reviews/model/card-review-ratings-provider'

import { useMediaQueries } from '@/shared/lib/hooks/useMediaQueries'
import { TagsCollapsed, TagsList } from '@/shared/ui/tags'

import { CardEntity } from '../../types/types'
import { CardTopPanel } from '../card-top-panel/card-top-panel'
import { Name } from '../name/name'
import { Price } from '../price/price'
import { ProductRating } from '../product-rating/ProductRating'
import { Slider } from '../slider/slider'

import cl from './card.module.scss'

export const Card = memo((props: CardEntity) => {
	const {
		className,
		type = 'regular',
		topController,
		bottomController,
		unavailable = false,
		adaptive = false,
		showPrice = true,
		blank = false,
		disableSlider = false,
		isRatingMobileCompact,
		onClick
	} = props

	const { productCode, title, images = [], tags = [], price = 0, oldPrice = 0, IS_NEW, reviewsSummary } = props.data
	const resolvedSummary = useCardReviewRating(productCode, reviewsSummary)
	const isReviewsLoading = resolvedSummary === null

	const link = `/products/productID${productCode}`
	const linkTitle = `Перейти к товару «${title}»`
	const target = blank ? `_blank` : `_self`
	const { isMobile } = useMediaQueries()
	const showOneTag = type === 'regular' && isMobile && !!tags?.length
	const [isTagsPanelOpened, setIsTagsPanelOpened] = useState(false)

	const cardClasses = clsx(cl.common, cl[type], className, {
		[cl.unavailable]: unavailable,
		[cl.adaptive]: adaptive
	})

	const sliderClasses = clsx(cl.slider, {
		[cl.disabled]: disableSlider
	})

	return (
		<article className={cardClasses} data-testid="card-entity">
			<CardTopPanel
				className={cl.topPanel}
				type={type}
				tagPanel={
					!isMobile ? (
						<TagsCollapsed
							tags={tags}
							isOpen={isTagsPanelOpened}
							onToggle={() => setIsTagsPanelOpened(prev => !prev)}
							isNew={IS_NEW}
							isNotAvailable={unavailable}
						/>
					) : (
						<TagsList tags={tags} isNew={IS_NEW} showOnlyFirst={showOneTag} isNotAvailable={unavailable} />
					)
				}
				control={topController}
			/>
			<Link href={link} title={linkTitle} target={target} className={cl.linkWrap} onClick={onClick} prefetch={false}>
				<Slider
					className={sliderClasses}
					type={type}
					images={images.slice(0, 4)}
					alt={title}
					dimmed={unavailable || isTagsPanelOpened}
				/>
			</Link>
			<div className={cl.bottomPanel}>
				<ProductRating
					className={cl.rating}
					productCode={productCode}
					summary={resolvedSummary ?? undefined}
					isMobileCompact={isRatingMobileCompact}
					showAverage={false}
					showEmptyReviewsText={!isReviewsLoading}
				/>
				<Link href={link} title={linkTitle} target={target} className={cl.nameLink} onClick={onClick} prefetch={false}>
					<Name className={cl.productName} type={type} title={title} unavailable={unavailable} />
					<Price type={type} price={price} oldPrice={oldPrice} showPrice={showPrice} unavailable={unavailable} />
				</Link>
			</div>
			{bottomController && <div className={cl.bottomController}>{bottomController}</div>}
		</article>
	)
})
